import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Header from "../../components/ui/Header";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import ProductCard from "./components/ProductCard";
import EarlyAccessForm from "./components/EarlyAccessForm";

const ProductsPortfolio = () => {
  const { t } = useTranslation();
  const [showEarlyAccess, setShowEarlyAccess] = useState(false);

  const products = [
    {
      id: "server-management",
      title: t("products.serverManagement.title"),
      subtitle: t("products.serverManagement.subtitle"),
      description: t("products.serverManagement.description"),
      features:
        t("products.serverManagement.features", { returnObjects: true }) || [],
      icon: "Server",
      color: "accent",
      status: "coming-soon",
    },
    {
      id: "custom-integrations",
      title: t("products.customIntegrations.title"),
      subtitle: t("products.customIntegrations.subtitle"),
      description: t("products.customIntegrations.description"),
      features:
        t("products.customIntegrations.features", { returnObjects: true }) ||
        [],
      icon: "Puzzle",
      color: "success",
      status: "coming-soon",
    },
    {
      id: "no-code-platform",
      title: t("products.noCodePlatform.title"),
      subtitle: t("products.noCodePlatform.subtitle"),
      description: t("products.noCodePlatform.description"),
      features:
        t("products.noCodePlatform.features", { returnObjects: true }) || [],
      icon: "Layers",
      color: "warning",
      status: "coming-soon",
    },
  ];

  const handleEarlyAccessSubmit = (formData) => {
    console.log("Early access form submitted:", formData);
    // Here you would typically send the data to your backend
    alert("Thank you for your interest! We will be in touch soon.");
    setShowEarlyAccess(false);
  };

  return (
    <>
      <Helmet>
        <title>{t("products.title")} - SIGNA Technologies</title>
        <meta name="description" content={t("products.description")} />
        <meta
          name="keywords"
          content="enterprise software, server management, custom integrations, no-code platform, SIGNA products"
        />
        <meta
          property="og:title"
          content={`${t("products.title")} - SIGNA Technologies`}
        />
        <meta property="og:description" content={t("products.description")} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${t("products.title")} - SIGNA Technologies`}
        />
        <meta name="twitter:description" content={t("products.description")} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="relative pt-24 pb-16 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5" />

          <div className="relative max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center justify-center space-x-2 mb-6"
              >
                <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Package" size={20} className="text-accent" />
                </div>
                <span className="text-sm font-medium text-accent">
                  {t("navigation.productsPortfolio")}
                </span>
              </motion.div>

              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                {t("products.title")}
              </motion.h1>

              <motion.p
                className="text-xl text-text-secondary max-w-3xl mx-auto mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {t("products.subtitle")}
              </motion.p>

              <motion.p
                className="text-lg text-text-secondary max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {t("products.description")}
              </motion.p>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                >
                  <ProductCard
                    product={product}
                    onEarlyAccess={() => setShowEarlyAccess(true)}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Early Access CTA */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="bg-gradient-to-r from-accent/10 via-primary/10 to-secondary/10 rounded-2xl p-8 md:p-12 text-center border border-accent/20"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-4">
                  <Icon name="Rocket" size={32} className="text-accent" />
                </div>
                <h2 className="text-3xl font-bold text-text-primary mb-4">
                  {t("products.earlyAccess.title")}
                </h2>
                <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                  {t("products.earlyAccess.description")}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  className="bg-accent hover:bg-accent/90"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => setShowEarlyAccess(true)}
                >
                  {t("products.earlyAccess.submit")}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  {t("scheduleCall")}
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-primary text-primary-foreground py-12 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                    <div className="w-4 h-4 bg-primary-foreground rounded-sm"></div>
                  </div>
                  <div>
                    <span className="text-xl font-bold">SIGNA</span>
                    <span className="text-sm opacity-80 ml-2">
                      Technologies
                    </span>
                  </div>
                </div>
                <p className="text-primary-foreground/80 mb-4 max-w-md">
                  {t("footer.description")}
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-4">{t("footer.services")}</h4>
                <ul className="space-y-2 text-sm text-primary-foreground/80">
                  <li>{t("footer.velocityEngineering")}</li>
                  <li>{t("footer.ownershipCulture")}</li>
                  <li>{t("footer.architecturalModernization")}</li>
                  <li>{t("footer.enterpriseConsulting")}</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-4">{t("footer.company")}</h4>
                <ul className="space-y-2 text-sm text-primary-foreground/80">
                  <li>{t("footer.aboutUs")}</li>
                  <li>{t("footer.caseStudies")}</li>
                  <li>{t("footer.knowledgeHub")}</li>
                  <li>{t("footer.contact")}</li>
                </ul>
              </div>
            </div>

            <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center text-sm text-primary-foreground/60">
              <p>
                &copy; {new Date().getFullYear()} SIGNA Technologies.{" "}
                {t("footer.allRightsReserved")}
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Early Access Modal */}
      <EarlyAccessForm
        isOpen={showEarlyAccess}
        onClose={() => setShowEarlyAccess(false)}
        onSubmit={handleEarlyAccessSubmit}
      />
    </>
  );
};

export default ProductsPortfolio;
